/**
 * Разбор результата занятия.
 *
 * Учебная единица режима — направление перехода (`ru → en` и `en → ru`), а не
 * клавиша после него (ТЗ FR-EN-25). Итог относится к переходам, а не к словам: слова здесь — повод сменить
 * раскладку, а не то, чему учит режим. К переходу относится ровно один
 * символ — первый после границы: именно там пользователь либо переключился,
 * либо забыл. Если он забыл и набрал в чужой раскладке всё слово, это всё
 * равно одна забытая смена раскладки, а не пять.
 *
 * Задержка перед этим символом — цена перехода. Отнести её к самой букве
 * значило бы обвинить букву в чужой медлительности.
 */

import type {
  KeystrokeEvent,
  MasteryStat,
  MasteryUnit,
  TypingError,
  UnitOutcome,
} from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { collectTokenOutcomes, type TokenSpan } from '@type2x/typing-engine';
import { applyUnitOutcomes, type LearningConfig } from '@type2x/learning-engine';

import type { LayoutSwitchPlan } from '../contracts/layout-switch.js';
import { switchUnitId } from './plan.js';

/**
 * Границы в виде вхождений, понятных общему сборщику итогов.
 *
 * Ключ вхождения — направление перехода, длина — один символ: та самая
 * позиция, на которой решается, переключился пользователь или нет.
 */
function boundarySpans(plan: LayoutSwitchPlan): TokenSpan[] {
  return plan.exercise.boundaries.map((boundary) => ({
    token: boundary.direction.id,
    start: boundary.position,
    length: 1,
  }));
}

/** Сопоставляет нажатия переходам занятия. */
export function collectSwitchOutcomes(
  plan: LayoutSwitchPlan,
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
): UnitOutcome[] {
  return collectTokenOutcomes(boundarySpans(plan), events, errors).map((outcome) => ({
    unitId: toMasteryUnitId(switchUnitId(outcome.token)),
    attempts: outcome.attempts,
    errors: outcome.errors,
    medianLatencyMs: outcome.medianLatencyMs,
    corrections: outcome.corrections,
  }));
}

/** Обновляет статистику переходов по итогам занятия. */
export function applySwitchOutcomes(
  units: readonly MasteryUnit[],
  stats: Readonly<Record<string, MasteryStat>>,
  outcomes: readonly UnitOutcome[],
  config: LearningConfig,
  nowIso: string,
): MasteryStat[] {
  return applyUnitOutcomes(units, stats, outcomes, config, nowIso);
}
